import React, { useState } from 'react';
import { AlertTriangle, Download, Plus } from 'lucide-react';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Table from '../../components/ui/Table';
import { useToast } from '../../components/ui/Toast';
import { products, stores } from '../../data/mockData';

function ExpiryStatus({ days }) {
  if (days < 0) return <Badge variant="error">🔴 Просрочен ({Math.abs(days)} дн. назад)</Badge>;
  if (days <= 3) return <Badge variant="urgent">🟠 Критический ({days} дн.)</Badge>;
  if (days <= 7) return <Badge variant="warning">🟡 Скоро ({days} дн.)</Badge>;
  return <Badge variant="success">🟢 Норма ({days} дн.)</Badge>;
}

export default function ExpiryMonitoring() {
  const toast = useToast();
  const [filter, setFilter] = useState('all');

  const sorted = [...products].sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry);
  const filtered = sorted.filter(p => {
    if (filter === 'expired') return p.daysUntilExpiry < 0;
    if (filter === 'critical') return p.daysUntilExpiry >= 0 && p.daysUntilExpiry <= 3;
    if (filter === 'warning') return p.daysUntilExpiry > 3 && p.daysUntilExpiry <= 7;
    return true;
  });

  const expired = products.filter(p => p.daysUntilExpiry < 0).length;
  const critical = products.filter(p => p.daysUntilExpiry >= 0 && p.daysUntilExpiry <= 3).length;
  const warning = products.filter(p => p.daysUntilExpiry > 3 && p.daysUntilExpiry <= 7).length;

  const columns = [
    {
      key: 'name', label: 'Товар',
      render: (v, row) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{v}</div>
          <code style={{ fontSize: 11, color: '#6B7280' }}>{row.barcode}</code>
        </div>
      )
    },
    { key: 'category', label: 'Категория', render: v => <Badge variant="primary">{v}</Badge> },
    { key: 'stockQuantity', label: 'Остаток', render: (v, row) => <span style={{ fontWeight: 600 }}>{v} {row.unit}</span> },
    {
      key: 'nearestExpiry', label: 'Дата истечения',
      render: (v, row) => (
        <span style={{
          fontWeight: 700, fontSize: 13,
          color: row.daysUntilExpiry < 0 ? '#FF3B30' : row.daysUntilExpiry <= 3 ? '#FF6B6B' : row.daysUntilExpiry <= 7 ? '#FF9500' : '#34C759',
        }}>
          {new Date(v).toLocaleDateString('ru-RU')}
        </span>
      )
    },
    { key: 'daysUntilExpiry', label: 'Статус', render: v => <ExpiryStatus days={v} /> },
    {
      key: 'actions', label: '', sortable: false,
      render: (_, row) => (
        <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
          <button onClick={() => toast(`ОСГ задача создана для "${row.name}"`, 'success')} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '1px solid #4A6CF7', background: '#EEF2FF', cursor: 'pointer', color: '#4A6CF7' }}>
            Создать задачу
          </button>
          <button onClick={() => toast(`"${row.name}" помечен как списан`, 'warning')} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '1px solid #E5E7EB', background: '#fff', cursor: 'pointer', color: '#FF9500' }}>
            Списать
          </button>
        </div>
      )
    }
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1A1A2E' }}>ОСГ Мониторинг — Сроки годности</h1>
          <p style={{ color: '#8E8E93', fontSize: 13, marginTop: 2 }}>Отсортировано по ближайшему сроку</p>
        </div>
        <Button variant="secondary" icon={<Download size={14} />} onClick={() => toast('Экспорт CSV', 'success')}>Экспорт CSV</Button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
        {[
          { label: 'Просрочено', value: expired, color: '#FF3B30', bg: '#FFF0F0', icon: '🔴' },
          { label: 'Критический (≤3 дн.)', value: critical, color: '#FF6B6B', bg: '#FFF5F5', icon: '🟠' },
          { label: 'Скоро (≤7 дн.)', value: warning, color: '#FF9500', bg: '#FFF7ED', icon: '🟡' },
          { label: 'Норма (>7 дн.)', value: products.length - expired - critical - warning, color: '#34C759', bg: '#F0FFF4', icon: '🟢' },
        ].map(s => (
          <div key={s.label} style={{ background: s.bg, borderRadius: 12, padding: '16px 20px', border: `1px solid ${s.color}22` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 22 }}>{s.icon}</span>
              <div>
                <div style={{ fontSize: 26, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 12, color: '#6B7280' }}>{s.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Card padding="0">
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #F3F4F6', display: 'flex', gap: 8 }}>
          {[
            { key: 'all', label: 'Все' },
            { key: 'expired', label: '🔴 Просроченные' },
            { key: 'critical', label: '🟠 Критические' },
            { key: 'warning', label: '🟡 Скоро истекут' },
          ].map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)} style={{
              padding: '6px 14px', borderRadius: 8, border: '1px solid #E5E7EB',
              background: filter === f.key ? '#4A6CF7' : '#fff',
              color: filter === f.key ? '#fff' : '#6B7280',
              fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            }}>{f.label}</button>
          ))}
        </div>
        <Table columns={columns} data={filtered} emptyText="Нет товаров" />
      </Card>
    </div>
  );
}
